import type React from "react";
import { Box, Small, P, FlexItem } from "orcs-design-system";
import { Link } from "react-router-dom";
import styled from "styled-components";
import themeGet from "@styled-system/theme-get";

import { RichTextContentViewer } from "src/shared/RichTextContent/RichTextContent";
import type { TagNavNode } from "./index.logic";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyComp = any;

const TagTreeHeaderTitleRouterLink = styled(Link as AnyComp)`
  cursor: pointer;
  border: none;
  background: none;
  appearance: none;
  justify-content: space-between;
  font-family: inherit;
  z-index: 2;
  display: flex;
  align-items: center;
  font-size: ${themeGet("fontSizes.1")};
  font-weight: ${themeGet("fontWeights.1")};
  text-align: left;
  text-decoration: none;
  color: inherit;
  margin-left: 0;
  line-height: 1;
  padding: 0;
  min-width: 0;
  flex: 1;
  overflow: hidden;

  &:hover,
  &:focus {
    color: inherit;
  }
`;

const TagTreeHeaderTitleLink = styled.a`
  cursor: pointer;
  border: none;
  background: none;
  appearance: none;
  justify-content: space-between;
  font-family: inherit;
  z-index: 2;
  display: flex;
  align-items: center;
  font-size: ${themeGet("fontSizes.1")};
  font-weight: ${themeGet("fontWeights.1")};
  text-align: left;
  text-decoration: none;
  color: inherit;
  margin-left: 0;
  line-height: 1;
  padding: 0;
  min-width: 0;
  flex: 1;
  overflow: hidden;

  &:hover,
  &:focus {
    color: inherit;
  }
`;

const TreeText = styled(P as AnyComp)`
  word-break: break-word;
  z-index: 2;
  transition: ${themeGet("transition.transitionDefault")};
`;

type TagTreeHeaderProps = {
  node: TagNavNode;
  onActivate?: () => void;
  getTagLinkTo?: (node: TagNavNode) => string | null;
};

const TagTreeHeader = ({
  node,
  onActivate,
  getTagLinkTo,
}: TagTreeHeaderProps) => {
  const { isTagType, isTagTypeDesc, name, tagValue } = node;

  if (isTagType) {
    const tagTypeHref = getTagLinkTo?.(node) ?? null;
    const tagTypeTitle = (
      <TreeText fontSize="1" data-testid="treeheader-node-name">
        {name}
      </TreeText>
    );

    if (tagTypeHref) {
      return (
        <TagTreeHeaderTitleRouterLink
          to={tagTypeHref}
          onClick={(e: React.MouseEvent) => {
            // Stop propagation so the row's runRowInteraction (toggle + onNodeSelected)
            // does not fire — navigation is handled by the Router Link itself.
            e.stopPropagation();
          }}
          style={{ minWidth: 0 }}
        >
          {tagTypeTitle}
        </TagTreeHeaderTitleRouterLink>
      );
    }

    return tagTypeTitle;
  }

  if (isTagTypeDesc) {
    const RTV = RichTextContentViewer as AnyComp;
    return (
      <Box data-testid="tag-type-desc-node" style={{ pointerEvents: "none" }}>
        <Small color="greyDark" style={{ display: "block" }}>
          <RTV content={node.details} fontSize="1.2rem" />
        </Small>
      </Box>
    );
  }

  const tagVal = tagValue as Record<string, unknown> | null | undefined;
  const appliedCount = tagVal?.["appliedCount"];

  const tagHref = getTagLinkTo?.(node) ?? null;

  const tagValueTitle = (
    <>
      <TreeText fontSize="1" data-testid="treeheader-node-name">
        {name}
      </TreeText>
      <FlexItem flex="0 0 auto">
        <Small color="subText">
          {appliedCount ? `${appliedCount} tagged` : ""}
        </Small>
      </FlexItem>
    </>
  );

  if (tagHref) {
    return (
      <TagTreeHeaderTitleLink
        href={tagHref}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onActivate?.();
        }}
        style={{ minWidth: 0 }}
      >
        {tagValueTitle}
      </TagTreeHeaderTitleLink>
    );
  }

  return <>{tagValueTitle}</>;
};

export default TagTreeHeader;
